import { useEffect, useState } from 'react';
import type { Asset } from '@/types/asset';
import type { DisposalDetails, DisposalAllocation } from '@/types/disposal';
import { dataService } from '@/services/data-service';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

interface DisposalAllocationStepProps {
  readonly asset: Asset;
  readonly details: DisposalDetails;
  readonly allocation: DisposalAllocation;
  readonly onDetailsChange: (details: DisposalDetails) => void;
  readonly onAllocationChange: (allocation: DisposalAllocation) => void;
  readonly onBack: () => void;
  readonly onNext: () => void;
}

export function DisposalAllocationStep({
  asset,
  details,
  allocation,
  onDetailsChange,
  onAllocationChange,
  onBack,
  onNext,
}: DisposalAllocationStepProps): React.ReactElement {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPreview = async () => {
      setLoading(true);
      setError(null);
      try {
        const preview = await dataService.getDisposalPreview({
          assetId: asset.id,
          disposalDate: details.date,
          disposeOfMode: details.disposeOfMode,
          disposalQuantity: details.disposalQuantity,
          depreciationHandling: details.depreciationHandling,
        });

        // Set the allocation with preview values
        onAllocationChange({
          actualCost: preview.actualCost ?? 0,
          actualDepreciation: preview.actualDepreciation ?? 0,
          historicalCost: preview.historicalCost ?? 0,
          historicalDepreciation: preview.historicalDepreciation ?? 0,
          actualCostPercentage: preview.actualCostPercentage ?? 100,
          actualDepreciationPercentage: preview.actualDepreciationPercentage ?? 100,
          historicalCostPercentage: preview.historicalCostPercentage ?? 100,
          historicalDepreciationPercentage: preview.historicalDepreciationPercentage ?? 100,
          proceedsValue: preview.proceedsValue ?? 0,
        });
      } catch (err: any) {
        console.error('Failed to load disposal preview:', err);
        setError(err.message || 'Failed to load preview data');
        // Set default values on error
        onAllocationChange({
          actualCost: 0,
          actualDepreciation: 0,
          historicalCost: 0,
          historicalDepreciation: 0,
          actualCostPercentage: 100,
          actualDepreciationPercentage: 100,
          historicalCostPercentage: 100,
          historicalDepreciationPercentage: 100,
          proceedsValue: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    loadPreview();
    // Only load on initial mount and when key details change (not disposeOfMode or disposalQuantity)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.id, details.date, details.depreciationHandling]);

  const handleValueChange = (field: keyof DisposalAllocation, value: string) => {
    const numValue = parseFloat(value) ?? 0;
    onAllocationChange({
      ...allocation,
      [field]: numValue,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{error}</p>
        </div>
        <div className="flex justify-between pt-4 border-t">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  const isPercentageMode = details.disposeOfMode === 'percentage';
  const isQuantityMode = details.disposeOfMode === 'quantity';

  const handleDisposeOfModeChange = (mode: 'value' | 'percentage' | 'quantity') => {
    // Update the details with the new mode
    const updatedDetails = { ...details, disposeOfMode: mode };
    
    // Reset disposal quantity when switching modes
    if (mode !== 'quantity') {
      updatedDetails.disposalQuantity = undefined;
    }
    
    // Trigger parent update
    onDetailsChange(updatedDetails);
  };

  const handleQuantityChange = (value: number | undefined) => {
    // Update quantity without triggering a full reload
    onDetailsChange({ ...details, disposalQuantity: value });
  };

  return (
    <div className="space-y-6">
      {/* Dispose Of Section */}
      <div className="space-y-4">
        <h3 className="text-base font-semibold">Dispose Of</h3>
        <div className="flex gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="disposeOfMode"
              value="value"
              checked={details.disposeOfMode === 'value'}
              onChange={() => handleDisposeOfModeChange('value')}
              className="h-4 w-4 text-primary focus:ring-primary"
            />
            <div className="text-sm">Value</div>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="disposeOfMode"
              value="percentage"
              checked={details.disposeOfMode === 'percentage'}
              onChange={() => handleDisposeOfModeChange('percentage')}
              className="h-4 w-4 text-primary focus:ring-primary"
            />
            <div className="text-sm">Percentage</div>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="disposeOfMode"
              value="quantity"
              checked={details.disposeOfMode === 'quantity'}
              onChange={() => handleDisposeOfModeChange('quantity')}
              className="h-4 w-4 text-primary focus:ring-primary"
            />
            <div className="text-sm">Quantity</div>
          </label>
        </div>

        {/* Current Quantity and Disposal Quantity */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2 col-span-1">
            <Label className="text-xs">Current Quantity</Label>
            <div className="rounded-lg border bg-muted/30 p-2 h-8 flex items-center justify-between">
              <span className="text-xs font-semibold">{details.currentQuantity}</span>
            </div>
          </div>

          {/* Quantity Input (only shown when quantity mode is selected) */}
          {isQuantityMode ? (
            <div className="space-y-2 col-span-1">
              <Label htmlFor="disposalQuantity" className="text-xs">
                Disposal Quantity <span className="text-destructive">*</span>
              </Label>
              <Input
                id="disposalQuantity"
                type="number"
                min="1"
                max={details.currentQuantity}
                value={details.disposalQuantity ?? ''}
                onChange={(e) => {
                  const value = parseInt(e.target.value) ?? undefined;
                  handleQuantityChange(value);
                }}
                placeholder={`Max: ${details.currentQuantity}`}
                className="h-8 text-sm"
              />
            </div>
          ) : (
            <div className="col-span-1"></div>
          )}
        </div>
      </div>

      {/* Allocation Section */}
      <div className="space-y-4">
        <h3 className="text-base font-semibold">Allocation</h3>

        {isPercentageMode ? (
          // Percentage Mode Table
          <div className="rounded-lg border">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 font-medium text-sm"></th>
                  <th className="text-left p-3 font-medium text-sm">Actual Cost %</th>
                  <th className="text-left p-3 font-medium text-sm">Historical Cost %</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-3 font-medium text-sm">Asset Cost</td>
                  <td className="p-3">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={allocation.actualCostPercentage ?? 100}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleValueChange('actualCostPercentage', e.target.value)}
                      className="w-full"
                    />
                  </td>
                  <td className="p-3">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={allocation.historicalCostPercentage ?? 100}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleValueChange('historicalCostPercentage', e.target.value)}
                      className="w-full"
                    />
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 font-medium text-sm">Depreciation</td>
                  <td className="p-3">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={allocation.actualDepreciationPercentage ?? 100}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleValueChange('actualDepreciationPercentage', e.target.value)}
                      className="w-full"
                    />
                  </td>
                  <td className="p-3">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={allocation.historicalDepreciationPercentage ?? 100}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleValueChange('historicalDepreciationPercentage', e.target.value)}
                      className="w-full"
                    />
                  </td>
                </tr>
                <tr>
                  <td className="p-3 font-medium text-sm">Proceeds Value</td>
                  <td className="p-3" colSpan={2}>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{asset.currency}</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={allocation.proceedsValue ?? 0}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleValueChange('proceedsValue', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          // Value/Quantity Mode Table
          <div className="rounded-lg border">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 font-medium text-sm"></th>
                  <th className="text-left p-3 font-medium text-sm">Actual Cost</th>
                  <th className="text-left p-3 font-medium text-sm">Historical Cost</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-3 font-medium text-sm">Asset Cost</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{asset.currency}</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={allocation.actualCost ?? 0}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleValueChange('actualCost', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{asset.currency}</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={allocation.historicalCost ?? 0}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleValueChange('historicalCost', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 font-medium text-sm">Depreciation</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{asset.currency}</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={allocation.actualDepreciation ?? 0}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleValueChange('actualDepreciation', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{asset.currency}</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={allocation.historicalDepreciation ?? 0}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleValueChange('historicalDepreciation', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="p-3 font-medium text-sm">Proceeds Value</td>
                  <td className="p-3" colSpan={2}>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">{asset.currency}</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={allocation.proceedsValue ?? 0}
                        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleValueChange('proceedsValue', e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex justify-between pt-4 border-t">
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90"
        >
          Next
        </button>
      </div>
    </div>
  );
}
